import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import {
  Shield,
  Truck,
  Radioactive,
  Crosshair,
  AirTrafficControl,
  BatteryCharging,
} from '@phosphor-icons/react';

const ITEMS = [
  { key: 'ballistic', target: 'ballistic', code: '01', Icon: Shield },
  { key: 'mobility', target: 'mobility', code: '02', Icon: Truck },
  { key: 'cbrne', target: 'cbrne', code: '03', Icon: Radioactive },
  { key: 'ammo', target: 'ammo', code: '04', Icon: Crosshair },
  { key: 'cuas', target: 'cuas', code: '05', Icon: AirTrafficControl },
  { key: 'battery', target: 'battery', code: '06', Icon: BatteryCharging },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function CapabilitiesIntro() {
  const { t } = useLanguage();
  return (
    <section
      id="capabilities"
      className="relative bg-[#0A1128] text-white overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-lines opacity-60 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3"
        >
          <span className="w-8 h-px bg-[#4F6355]" />
          <span className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#4F6355]">
            {t.capabilitiesIntro.overline}
          </span>
        </motion.div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="lg:col-span-7 font-display font-bold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]"
          >
            {t.capabilitiesIntro.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 text-base md:text-lg text-[#94A3B8] leading-relaxed"
          >
            {t.capabilitiesIntro.body}
          </motion.p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {ITEMS.map(({ key, target, code, Icon }, idx) => (
            <motion.button
              key={key}
              onClick={() => scrollToId(target)}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.55, delay: idx * 0.07 }}
              className="group relative bg-[#0A1128] hover:bg-[#151F32] transition-colors p-6 md:p-7 text-left min-h-[200px] flex flex-col justify-between"
            >
              <div className="flex items-start justify-between">
                <Icon size={28} weight="duotone" className="text-[#4F6355] group-hover:text-white transition-colors" />
                <span className="font-mono text-[10px] tracking-[0.3em] text-[#94A3B8]">{code}</span>
              </div>
              <div className="mt-6">
                <h3 className="font-display font-semibold text-base md:text-lg leading-tight tracking-tight text-white">
                  {t[key].title}
                </h3>
                <span className="block mt-3 w-6 h-px bg-white/10 group-hover:bg-[#4F6355] group-hover:w-12 transition-all duration-500" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
